import { type FC, type RefObject, useLayoutEffect } from "react";

import { useElementSize, useMergedRef } from "@mantine/hooks";
import { Spacing } from "@snapchat/snap-design-system";
import { type TooltipContentProps } from "recharts";

import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import { AwardBarKind } from "../../models";
import { type AwardChartDatum, formatAwardMonthLabel, isAwardChartDatum } from "../../utils";

import { tooltip, tooltipDetail, tooltipTitle } from "./AwardHistoryTooltip.module.css";
import { AwardDetailLine, NoAwardThisMonth, SelectionsInProgress } from "./messages.generated";

const AwardDetail: FC<{ datum: AwardChartDatum }> = ({ datum }) => {
    switch (datum.kind) {
        case AwardBarKind.Awarded:
            return (
                <>
                    {datum.awards.map((award, index) => (
                        <div key={index}>
                            <AwardDetailLine lens={award.lensName} amount={formatMilliUsd(award.amountMilliUsd)} />
                        </div>
                    ))}
                </>
            );
        case AwardBarKind.InProgress:
            return <SelectionsInProgress />;
        case AwardBarKind.Empty:
            return <NoAwardThisMonth />;
    }
};

interface AwardHistoryTooltipProps extends Partial<Pick<TooltipContentProps, "active" | "payload" | "coordinate">> {
    viewport?: RefObject<HTMLDivElement>;
}

export const AwardHistoryTooltip: FC<AwardHistoryTooltipProps> = ({ active, payload = [], coordinate, viewport }) => {
    const { ref, width, height } = useElementSize<HTMLDivElement>();
    // Mantine’s nullable element type needs a callback adapter for React 18 DOM refs.
    const tooltipRef = useMergedRef(ref);
    useLayoutEffect(() => {
        const element = ref.current;
        const chartViewport = viewport?.current;
        if (!active || !element || !chartViewport || !coordinate) return;
        const updatePosition = () => {
            const tooltipWidth = element.offsetWidth;
            const tooltipHeight = element.offsetHeight;
            const anchorX = coordinate.x - chartViewport.scrollLeft;
            const left = Math.max(
                Spacing.XS,
                Math.min(anchorX - tooltipWidth / 2, chartViewport.clientWidth - tooltipWidth - Spacing.XS),
            );
            const top = Math.max(Spacing.XS, coordinate.y - tooltipHeight - Spacing.XS);
            element.style.setProperty("--award-tooltip-left", `${left}px`);
            element.style.setProperty("--award-tooltip-top", `${top}px`);
        };
        updatePosition();
        chartViewport.addEventListener("scroll", updatePosition, { passive: true });
        return () => {
            chartViewport.removeEventListener("scroll", updatePosition);
        };
    }, [active, coordinate, height, ref, viewport, width]);
    const datum: unknown = payload[0]?.payload;
    const visible = active && isAwardChartDatum(datum);

    return (
        <div ref={tooltipRef} className={tooltip} role="status" hidden={!visible}>
            {visible && (
                <>
                    <div className={`subheading-sh500 ${tooltipTitle}`}>{formatAwardMonthLabel(datum.monthStart)}</div>
                    <div className={`paragraph-p300 ${tooltipDetail}`}>
                        <AwardDetail datum={datum} />
                    </div>
                </>
            )}
        </div>
    );
};
